/* __next_internal_client_entry_do_not_use__ RetailerFooter auto */ import { jsx as _jsx } from "react/jsx-runtime";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { POS_MARKETING_BANNERS_DISMISSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/POS_MARKETING_BANNERS_DISMISSED";
import { IRetailerLinkCohortEnum } from "@faire/web-api--source/indigofair/retailers/pos/IRetailerLinkCohortEnum";
import { observer } from "mobx-react";
import * as React from "react";
import { POSCohortFooterBanner } from "@faire/retailer-visitor-shared/features/PointOfSale/components/POSCohortFooterBanner";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { nextValue } from "@faire/retailer-visitor-shared/lib/nextValue";
import { getSettingEnableOptInNudge } from "@faire/retailer-visitor-shared/settings/getSettingEnableOptInNudge";
import { RetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { AppDownloadCTA } from "@faire/retailer-visitor-shared/components/Footer/AppDownloadCTA";
import { Footer } from "@faire/retailer-visitor-shared/components/Footer/index";
import { OptInNudgeFooterBanner } from "@faire/retailer-visitor-shared/components/Footer/OptInNudgeFooterBanner";
import { RetailerToMakerReferral } from "@faire/retailer-visitor-shared/components/Footer/RetailerToMakerReferral";
var FooterBannerContent;
(function(FooterBannerContent) {
    FooterBannerContent[FooterBannerContent["NONE"] = 0] = "NONE";
    FooterBannerContent[FooterBannerContent["R2B_REFERRAL"] = 1] = "R2B_REFERRAL";
    FooterBannerContent[FooterBannerContent["POS_COHORT"] = 2] = "POS_COHORT";
    FooterBannerContent[FooterBannerContent["APP_DOWNLOAD"] = 3] = "APP_DOWNLOAD";
    FooterBannerContent[FooterBannerContent["OPT_IN_NUDGE"] = 4] = "OPT_IN_NUDGE";
})(FooterBannerContent || (FooterBannerContent = {}));
var getFooterBannerContent = function(hideReferral, retailerLinkCohort, hasDismissedPosBanner) {
    // Logged out user sees nothing
    if (!isLoggedInRetailer()) {
        return FooterBannerContent.NONE;
    }
    var _RetailerStore_get = RetailerStore.get(), showR2B = _RetailerStore_get.showR2B, isNewUser = _RetailerStore_get.isNewUser, showContextualAppDownloadCTAs = _RetailerStore_get.showContextualAppDownloadCTAs, isUnsubscribedToMarketingEmails = _RetailerStore_get.isUnsubscribedToMarketingEmails;
    var shouldShowOptInNudgeBanner = isUnsubscribedToMarketingEmails && getSettingEnableOptInNudge();
    var shouldShowPOSCohortBanner = SUPPORTED_POS_BANNER_COHORTS.includes(retailerLinkCohort) && (!hasDismissedPosBanner || retailerLinkCohort === IRetailerLinkCohortEnum.RetailerLinkCohort.INACTIVE);
    // We don't display any advanced features for new retailers
    if (isNewUser) {
        return FooterBannerContent.NONE;
    }
    var possibleBanners = [];
    // Should show R2B
    if (!hideReferral && showR2B) {
        possibleBanners.push(FooterBannerContent.R2B_REFERRAL);
    }
    // Should show app download banner
    if (showContextualAppDownloadCTAs) {
        possibleBanners.push(FooterBannerContent.APP_DOWNLOAD);
    }
    // Should show opt in nudge banner
    if (shouldShowOptInNudgeBanner) {
        possibleBanners.push(FooterBannerContent.OPT_IN_NUDGE);
    }
    if (shouldShowPOSCohortBanner) {
        possibleBanners.push(FooterBannerContent.POS_COHORT);
    }
    if (possibleBanners.length === 0) {
        return FooterBannerContent.NONE;
    }
    var _nextValue;
    return (_nextValue = nextValue(possibleBanners, FOOTER_BANNERS_SEEN_KEY)) !== null && _nextValue !== void 0 ? _nextValue : FooterBannerContent.NONE;
};
var RetailerFooterBase = function(props) {
    var isSSR = useIsSSR();
    var retailerLinkCohort = usePOSSummary().retailerLinkCohort;
    var _useMilestone = useMilestone(POS_MARKETING_BANNERS_DISMISSED), hasDismissedPosBanner = _useMilestone.hasOccurred;
    var banner = function() {
        if (isSSR) {
            // TODO(jocelyn): getFooterBannerContent has a race condition which breaks SSR. Investigate.
            return /*#__PURE__*/ _jsx(React.Fragment, {});
        }
        var _props_hideReferral;
        switch(getFooterBannerContent((_props_hideReferral = props.hideReferral) !== null && _props_hideReferral !== void 0 ? _props_hideReferral : false, retailerLinkCohort, hasDismissedPosBanner)){
            case FooterBannerContent.R2B_REFERRAL:
                return /*#__PURE__*/ _jsx(RetailerToMakerReferral, {});
            case FooterBannerContent.POS_COHORT:
                return /*#__PURE__*/ _jsx(POSCohortFooterBanner, {});
            case FooterBannerContent.APP_DOWNLOAD:
                return /*#__PURE__*/ _jsx(AppDownloadCTA, {});
            case FooterBannerContent.OPT_IN_NUDGE:
                return /*#__PURE__*/ _jsx(OptInNudgeFooterBanner, {});
            case FooterBannerContent.NONE:
            default:
                return null;
        }
    }();
    return /*#__PURE__*/ _jsx(Footer, {
        className: props.className,
        noTopBorder: props.noTopBorder,
        noTopMargin: props.noTopMargin,
        children: banner
    });
};
export var RetailerFooter = observer(RetailerFooterBase);
var FOOTER_BANNERS_SEEN_KEY = "FOOTER_BANNERS_SEEN";
var SUPPORTED_POS_BANNER_COHORTS = [
    IRetailerLinkCohortEnum.RetailerLinkCohort.NEW,
    IRetailerLinkCohortEnum.RetailerLinkCohort.CHURNED,
    IRetailerLinkCohortEnum.RetailerLinkCohort.INACTIVE
];
